// PenPopup — Classic 常設フロート「ペン」(ペン種ピッカー) の floating panel.
//
// 背景 (consult 2026-06-20, A 採択): Classic のペン種選択を **単一の常設フロート「ペン」**
// に集約する。これまで ToolPanel に並んでいたインライン ペン種 fieldset
// ([ブラシ][サインペン]) + [テクスチャ] 入口 + 旧 `TexturePopup` を、本フロート 1 個に
// 吸収する。ToolPanel には [ペン] トグル入口 ([プレビュ]/[レイヤー] 同型) だけを置き、
// 押下で本フロートを開閉する (= ボタンを増やさず ToolPanel の縦圧を解消、§7 / §1.1)。
//
// §9.3 (consult 2026-05-09)「懐古性は配色・配置・操作感で維持、ペン種ボタンの"数"では
// 維持しない」: Java 版に [ブラシ]/[サインペン] は無く新版の構築物なので、フロート集約は
// 懐古純度を損なわない。常設フロート (= 開きっぱなしにできる) なので、ペン切替は 1 クリック
// のまま (ドックから浮くだけ)。
//
// 設計 (LayerPopup / PreviewOverlay / 旧 TexturePopup と同流儀):
//   - position: fixed、ヘッダ (タイトルバー) を掴んでドラッグ移動。位置は per-user に
//     localStorage 記憶 (`penPopup.ts`)。
//   - 開閉は ClassicLayout が visible state を持ち入口ボタンで切替える (= LayerPopup の
//     visibility toggle と同型、可視 state も localStorage 永続化)。本 panel 内には ✕ を
//     付けず、戻し方は入口ボタン一本。
//   - 中身は Modern と共有する `PenKindPicker` (ペン種チップ + texture preset)。pen 切替・
//     penParams 反映は呼び出し側 (共有 chrome の onSelectPen / onSelectTexturePreset、
//     penParamsFor) が担う (= engine / protocol 不変)。
//
// 位置 / 永続化のデータモデルは `penPopup.ts` (pure helper、node vitest 対象) に集約し、
// 本 component はそれを組み合わせて描画するだけ (LayerPopup と同じ分離)。
//
// S3 スライス1 (floating-panels-2026-08-01.md §8): drag 移動は S2 共有
// `usePanelDragGrip` + `floatShellGeometry` を使う (= ColorWheelPalette / LayerPopup と
// 同じ移行。旧実装は `preventDefault()` を呼んでおらずネイティブ drag/選択が漏れる潜在
// バグを抱えていた。挙動不変: clamp margin は旧 `clampPopupPosition` の既定 8px をそのまま
// 渡す)。

import type { StrokePen, TexturePreset } from '@oekaki/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  clampFloatPosition,
  type FloatPoint,
  floatPositionFromPointer,
} from '../shared/floatShellGeometry.js';
import { PenKindPicker } from '../shared/PenKindPicker.js';
import { usePanelDragGrip } from '../shared/usePanelDragGrip.js';
import type { SidebarDockSide } from './classicToolFloat.js';
import type { FloatingPopupPosition } from './layerPopup.js';
import { insetFrame, outsetFrame } from './legacyFrames.js';
import {
  defaultPenPopupPosition,
  PEN_POPUP_NOMINAL_HEIGHT,
  PEN_POPUP_NOMINAL_WIDTH,
  readPenPopupPosition,
  writePenPopupPosition,
} from './penPopup.js';

/** 旧 `clampPopupPosition` の既定 margin (8px) と揃える (= 挙動不変)。 */
const POPUP_EDGE_INSET_PX = 8;

export interface PenPopupProps {
  /** 現在のペン種 (= チップのハイライト対象、`PenConfig.pen`)。 */
  readonly pen: StrokePen;
  /**
   * ペン種チップ選択で発火。caller (ClassicLayout) は共有 chrome の `onSelectPen` に流す
   * (= `pen=kind` をセット)。texture を選ぶと共有 chrome 経路で penParams が載る。
   */
  readonly onSelectPen: (
    kind: Extract<
      StrokePen,
      'brush' | 'flat' | 'texture' | 'tone' | 'airbrush' | 'crayon' | 'pastel' | 'dot'
    >,
  ) => void;
  /** 現在選択中の texture preset (= `pen==='texture'` のとき preset サムネのハイライト)。 */
  readonly texturePreset: TexturePreset;
  /**
   * texture preset サムネクリックで発火。caller (ClassicLayout) は共有 chrome の
   * `onSelectTexturePreset` に流す (= pen.texturePreset を書く)。あわせて caller 側で
   * pen='texture' に切替える (= まだ texture を選んでいないなら入る)。
   */
  readonly onSelectTexturePreset: (preset: TexturePreset) => void;
  /**
   * エアブラシ grain/density (airbrush-design-2026-08-15.md §6、表現パリティ原則で
   * Classic にも露出)。`PenKindPicker` へそのまま横流しする。
   */
  readonly airbrushGrain: number;
  readonly onAirbrushGrainChange: (grain: number) => void;
  readonly airbrushDensity: number;
  readonly onAirbrushDensityChange: (density: number) => void;
  /**
   * クレヨン/パステルの目の粗さ (phase11-light-pens-2026-08-21.md §2.6 / §6、表現パリティ
   * 原則で Classic にも露出)。`PenKindPicker` へそのまま横流しする。
   */
  readonly crayonGrain: number;
  readonly onCrayonGrainChange: (grain: number) => void;
  readonly pastelGrain: number;
  readonly onPastelGrainChange: (grain: number) => void;
  /**
   * トーンの番手 (density) / 間隔 (pitch)、ドットの間隔 (spacing) — 封印解除 + 新規露出
   * (同 §4.5-§4.7 / §3 / §6、表現パリティ原則で Classic にも露出)。`PenKindPicker` へ
   * そのまま横流しする。
   */
  readonly toneDensity: number;
  readonly onToneDensityChange: (density: number) => void;
  readonly tonePitch: number;
  readonly onTonePitchChange: (pitch: number) => void;
  readonly dotSpacing: number;
  readonly onDotSpacingChange: (spacing: number) => void;
  /**
   * 2026-08-01 追加依頼: Classic サイドバーの現在のドック側 (`classicToolFloat.ts`
   * §12)。既定 `'left'` は従来どおりの右上初期位置。`'right'` のときだけ初期位置を
   * 左上へミラーする (LayerPopup と同じ理由・同じ仕組み)。既に localStorage に位置が
   * 保存されているときはこの prop を無視する (ユーザーが動かした位置を最優先)。
   */
  readonly dockSide?: SidebarDockSide;
}

export const PenPopup = ({
  pen,
  onSelectPen,
  texturePreset,
  onSelectTexturePreset,
  airbrushGrain,
  onAirbrushGrainChange,
  airbrushDensity,
  onAirbrushDensityChange,
  crayonGrain,
  onCrayonGrainChange,
  pastelGrain,
  onPastelGrainChange,
  toneDensity,
  onToneDensityChange,
  tonePitch,
  onTonePitchChange,
  dotSpacing,
  onDotSpacingChange,
  dockSide = 'left',
}: PenPopupProps): React.ReactNode => {
  // 位置 = localStorage 復元 → 無ければ画面右上寄り (描画の邪魔になりにくい隅、
  // dockSide==='right' なら左上へミラー、2026-08-01 追加依頼)。
  // lazy initializer で 1 回だけ算出 (LayerPopup / 旧 TexturePopup と同流儀)。
  const [pos, setPos] = useState<FloatingPopupPosition>(() => {
    if (typeof window === 'undefined') return defaultPenPopupPosition(0, 0, dockSide);
    const stored = readPenPopupPosition(window.localStorage);
    if (stored !== null) return stored;
    return defaultPenPopupPosition(window.innerWidth, window.innerHeight, dockSide);
  });
  // 2026-08-01 追加依頼、実機検証で発覚した抜け穴の修正 (LayerPopup.tsx と同型):
  // 本 popup も「出しっぱなし」で remount しないため、mount 時点のミラーだけでは
  // 「同一セッション内で後からサイドバーを右ドックへドラッグする」実際の repro を
  // 救えない。**ユーザーがまだ動かしていない場合に限り**、dockSide が変わるたびに
  // 再ミラーする。
  const hasUserPositionedRef = useRef<boolean>(
    typeof window !== 'undefined' && readPenPopupPosition(window.localStorage) !== null,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || hasUserPositionedRef.current) return;
    setPos(defaultPenPopupPosition(window.innerWidth, window.innerHeight, dockSide));
  }, [dockSide]);
  const panelRef = useRef<HTMLElement | null>(null);

  const measurePanel = useCallback((): { width: number; height: number } => {
    const el = panelRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    return { width: PEN_POPUP_NOMINAL_WIDTH, height: PEN_POPUP_NOMINAL_HEIGHT };
  }, []);

  const persistPosition = useCallback((next: FloatingPopupPosition): void => {
    if (typeof window === 'undefined') return;
    writePenPopupPosition(window.localStorage, next);
  }, []);

  // 実測 grab-offset (= 掴んだ点と panel 左上の相対距離、S2 §11 と同じ流儀).
  const grabOffsetRef = useRef<FloatPoint | null>(null);
  const grip = usePanelDragGrip({
    onDragStart: (offset) => {
      grabOffsetRef.current = offset;
    },
    onDragMove: (clientX, clientY) => {
      const offset = grabOffsetRef.current ?? { x: 0, y: 0 };
      setPos(
        clampFloatPosition(
          floatPositionFromPointer(clientX, clientY, offset),
          measurePanel(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        ),
      );
    },
    onDragEnd: (committed) => {
      grabOffsetRef.current = null;
      if (committed) {
        // 2026-08-01 追加依頼: ここでユーザーが実際に動かしたと確定する。以後は
        // dockSide が変わっても上の再ミラー effect が発火しないようにする。
        hasUserPositionedRef.current = true;
        setPos((cur) => {
          persistPosition(cur);
          return cur;
        });
      }
    },
    measureGrabOffset: (e) => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  // 初回 clamp: 復元位置が小さい画面ではみ出すケースを mount 後に画面内へ引き戻す。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPos((cur) => {
      const clamped = clampFloatPosition(
        cur,
        measurePanel(),
        { width: window.innerWidth, height: window.innerHeight },
        POPUP_EDGE_INSET_PX,
      );
      if (clamped.x !== cur.x || clamped.y !== cur.y) persistPosition(clamped);
      return clamped;
    });
  }, [measurePanel, persistPosition]);

  // window resize で画面外に取り残されたら引き戻す。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = (): void => {
      setPos((cur) => {
        const clamped = clampFloatPosition(
          cur,
          measurePanel(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        );
        if (clamped.x !== cur.x || clamped.y !== cur.y) persistPosition(clamped);
        return clamped;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measurePanel, persistPosition]);

  return (
    <section
      ref={panelRef}
      data-testid="classic-pen-popup"
      aria-label="ペン"
      className={`fixed z-40 flex w-[240px] flex-col bg-[var(--color-panel)] text-white select-none ${outsetFrame}`}
      style={{ left: pos.x, top: pos.y }}
    >
      {/* タイトルバー = drag handle (LayerPopup / PreviewOverlay と同じペンタブ対策:
          touchAction:none + 共有 usePanelDragGrip)。閉じるボタンは無い (= 入口ボタンで開閉)。 */}
      <div
        data-testid="classic-pen-popup-header"
        className="flex shrink-0 cursor-move items-center justify-between border-b border-black bg-[var(--color-bg-soft)] px-2 py-1 text-[10px] font-bold"
        style={{ touchAction: 'none' }}
        onPointerDown={grip.onPointerDown}
        onPointerMove={grip.onPointerMove}
        onPointerUp={grip.onPointerUp}
        onPointerCancel={grip.onPointerCancel}
        onLostPointerCapture={grip.onLostPointerCapture}
      >
        <span>ペン</span>
      </div>

      {/* ペン種チップ + (texture のとき) preset サムネ (Modern と共有 `PenKindPicker`)。
          クリックで onSelectPen / onSelectTexturePreset → caller が共有 chrome 経路で
          pen / penParams を反映 (= engine 不変)。 */}
      <div className={`m-1 p-1 ${insetFrame}`}>
        <PenKindPicker
          pen={pen}
          onSelectPen={onSelectPen}
          texturePreset={texturePreset}
          onSelectTexturePreset={onSelectTexturePreset}
          airbrushGrain={airbrushGrain}
          onAirbrushGrainChange={onAirbrushGrainChange}
          airbrushDensity={airbrushDensity}
          onAirbrushDensityChange={onAirbrushDensityChange}
          crayonGrain={crayonGrain}
          onCrayonGrainChange={onCrayonGrainChange}
          pastelGrain={pastelGrain}
          onPastelGrainChange={onPastelGrainChange}
          toneDensity={toneDensity}
          onToneDensityChange={onToneDensityChange}
          tonePitch={tonePitch}
          onTonePitchChange={onTonePitchChange}
          dotSpacing={dotSpacing}
          onDotSpacingChange={onDotSpacingChange}
        />
      </div>
    </section>
  );
};
